/**
 * Narrowing the client list: search, filter, sort.
 *
 * All of it local, over the whole 614-row set that `fetchAllLedger` pulls in one
 * call. That is a deliberate consequence of the API having no search of its own —
 * see the note on `fetchAllLedger`. What it buys the reader is the part that
 * matters: a search here looks at every client, not just the ones already on
 * screen, so an empty result means the client is not there rather than not
 * downloaded yet.
 *
 * Pure functions, taking rows and returning rows. No React, no fetching — which
 * is what lets the matching rules be checked against real client names without
 * standing a screen up.
 */

/** The three filter axes, each with "everything" as the default. */
export const SIDE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Cr', label: 'Cr' },
]

/**
 * Where a client has got to in this period's collection.
 *
 * Derived from our own request rather than the ledger — the ledger knows the
 * balance, not whether anybody has asked for a statement. Most of the 614 have no
 * request at all, so `none` is the common case and is named for what it is.
 */
export const COLLECTION_OPTIONS = [
  { value: 'all', label: 'Any status' },
  { value: 'none', label: 'Not asked yet' },
  { value: 'waiting', label: 'Waiting on them' },
  { value: 'received', label: 'Statement received' },
]

export const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All parties' },
  { value: 'customer', label: 'Customers' },
  { value: 'supplier', label: 'Suppliers' },
]

/**
 * Whether a client is on the daily scheduled chase list.
 *
 * Its own filter rather than folded into `COLLECTION_OPTIONS`: enrollment and
 * collection progress are independent facts — a client can be freshly enrolled
 * and still "not asked yet", or long received and never enrolled at all — so
 * one dropdown answering both would mean some combination of the two always
 * reads as a state that isn't really there.
 */
export const CHASE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'on', label: 'On daily chase' },
  { value: 'off', label: 'Not on daily chase' },
]

/**
 * Whether the system itself is failing to reach this client — a config or
 * template problem on our side, not anything about the client. Its own
 * axis, orthogonal to every filter above: those are all about what a
 * client has or hasn't said, this one is about whether our messages are
 * even leaving the building.
 */
export const PROBLEM_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'send_failing', label: 'Sending problem' },
]

/**
 * Where a client sits relative to reconciliation — separate from
 * `COLLECTION_OPTIONS` above, which only knows whether their statement has
 * arrived, not what happened to it after. `collecting` covers everything
 * before a successful "real flow" reconciliation run exists for this
 * request (see ledgerReconcile.service.js#submitRealReconciliation) —
 * including a request that's `READY` but never actually reconciled.
 * `post_reconciliation_chase` is the one this filter exists for: a
 * reconciliation found something on the client's own side
 * (`rectificationSide` 'client' or 'both'), so the collection is done but
 * there's a new, different reason to be talking to them.
 */
export const RECONCILIATION_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'collecting', label: 'Not reconciled yet' },
  { value: 'post_reconciliation_chase', label: 'Reconciled — client to fix' },
  { value: 'reconciled_clean', label: 'Reconciled — clean' },
]

/**
 * What ascending and descending actually mean, per field.
 *
 * "Ascending" is a word about the sort algorithm; "A–Z" and "High first" are
 * words about what the reader will see. The pairs differ by field because
 * ascending names and ascending money read nothing alike, and a single generic
 * label would leave someone clicking to find out which way is which.
 */
const DIRECTION_LABELS = {
  name: { asc: 'A–Z', desc: 'Z–A' },
  balance: { asc: 'Low first', desc: 'High first' },
}

/** The direction label for the current field, for the toggle to show. */
export function directionLabel(q) {
  return (DIRECTION_LABELS[q.sortBy] ?? DIRECTION_LABELS.name)[q.sortDir === 'desc' ? 'desc' : 'asc']
}

export const DEFAULT_QUERY = {
  text: '',
  side: 'all',
  collection: 'all',
  category: 'all',
  chase: 'all',
  problem: 'all',
  reconciliation: 'all',
  sortBy: 'name',
  sortDir: 'asc',
}

/** Is any filter actually doing something? Decides whether "Clear" is offered. */
export function isNarrowed(q) {
  return (
    Boolean(q.text.trim()) ||
    q.side !== 'all' ||
    q.collection !== 'all' ||
    q.category !== 'all' ||
    q.chase !== 'all' ||
    q.problem !== 'all' ||
    q.reconciliation !== 'all'
  )
}

/**
 * How far along this client's collection is.
 *
 * Reads the request's own `status` where there is one, because that is the field
 * the backend maintains and second-guessing it here would let the filter and the
 * row's own text disagree. `READY` and `RECEIVED` both count as received: the
 * distinction between them is whether our side reconciles, which is a different
 * question from whether the client sent anything.
 */
function collectionStateOf(request) {
  if (!request || !request.status) return 'none'
  if (request.status === 'RECEIVED' || request.status === 'READY') return 'received'
  if (request.status === 'NOT_REQUESTED') return 'none'
  return 'waiting'
}

/**
 * Where this client sits relative to reconciliation — `runFor` looks up the
 * latest run by this request's own `_id` (see `api.js#latestReconciliationRuns`).
 * A request with no run at all (no "real flow" reconciliation has ever been
 * submitted for it, or it's not `READY` yet) reads as `collecting` — the
 * common case, same as `collectionStateOf`'s own `none`/`waiting` default.
 */
export function reconciliationPhaseOf(request, runFor = () => null) {
  if (!request) return 'collecting'
  const run = runFor(request._id)
  if (!run || run.status !== 'success') return 'collecting'
  if (run.rectificationSide === 'client' || run.rectificationSide === 'both') return 'post_reconciliation_chase'
  return 'reconciled_clean'
}

/**
 * Does this client match what was typed?
 *
 * Name and code, both, and each on a plain case-insensitive substring — because
 * the two things people have in front of them are a name off an invoice and a
 * party code out of the ERP, and they should not have to know which box to use.
 *
 * Every word must match, in any order and in either field: "unique jewels" finds
 * "1 Unique Jewels Private Limited" even though the name does not start with it,
 * and "jewels unique" finds it too. Requiring all the words is what keeps a
 * two-word search from widening the result instead of narrowing it.
 */
function matchesText(row, text) {
  const needle = String(text ?? '').trim().toLowerCase()
  if (!needle) return true
  const hay = `${row.clientName ?? ''} ${row.clientCode ?? ''}`.toLowerCase()
  return needle.split(/\s+/).every((word) => hay.includes(word))
}

/** Customer or supplier, from the ledger's free-text category. */
function categoryOf(row) {
  const c = String(row.category ?? '').toLowerCase()
  if (c.includes('debtor') || c.includes('customer')) return 'customer'
  if (c.includes('creditor') || c.includes('supplier')) return 'supplier'
  return 'other'
}

/**
 * The rows to show, in order.
 *
 * `requestFor` is passed in rather than the joined rows being built here, so this
 * stays a function of the ledger plus a lookup and does not need to know how the
 * screen shapes its placeholder rows.
 *
 * Name sort uses `localeCompare` with `numeric`, so "24 International" sorts
 * before "1000 Gems" the way a person reads them rather than the way ASCII does.
 *
 * Direction is applied by flipping the comparator's sign rather than reversing
 * the sorted array. Reversing looks equivalent and is not: it would also flip the
 * name tie-break under a balance sort, so two clients on an identical balance
 * would swap places purely because the direction changed. Negating keeps ties
 * broken by name ascending in both directions, which is the stable, unsurprising
 * behaviour.
 */
export function selectClients(rows, q, requestFor = () => null, isAutoChasing = () => false, runFor = () => null) {
  const out = rows.filter((row) => {
    if (!matchesText(row, q.text)) return false
    if (q.side !== 'all' && row.side !== q.side) return false
    if (q.category !== 'all' && categoryOf(row) !== q.category) return false
    if (q.collection !== 'all' && collectionStateOf(requestFor(row.clientCode)) !== q.collection) {
      return false
    }
    if (q.chase !== 'all') {
      const on = isAutoChasing(row.clientCode)
      if (q.chase === 'on' && !on) return false
      if (q.chase === 'off' && on) return false
    }
    if (q.problem === 'send_failing' && !requestFor(row.clientCode)?.conversation?.sendFailed) {
      return false
    }
    if (q.reconciliation !== 'all' && reconciliationPhaseOf(requestFor(row.clientCode), runFor) !== q.reconciliation) {
      return false
    }
    return true
  })

  const byName = (a, b) =>
    String(a.clientName ?? '').localeCompare(String(b.clientName ?? ''), 'en-IN', {
      numeric: true,
      sensitivity: 'base',
    })

  const flip = q.sortDir === 'desc' ? -1 : 1

  // Sorted on a copy of the filtered array — `filter` already returned a new one,
  // so this does not disturb the caller's rows.
  return q.sortBy === 'balance'
    ? out.sort((a, b) => flip * ((a.balancePaise ?? 0) - (b.balancePaise ?? 0)) || byName(a, b))
    : out.sort((a, b) => flip * byName(a, b))
}
